import React, { useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import Heading from '../../components/Heading'
import Pagination from '../../components/Pagination'
import { showToast } from '../../services/ToastService'
import { fetchOnboardingCustomers } from '../../services/OnboardingCustomerService'
import type {
    OnboardingCustomerPagination,
    OnboardingCustomerRow,
} from '../../types/OnboardingCustomer'
import styles from './CustomersOverview.module.css'

const ITEMS_PER_PAGE = 20

function getInitials(name: string): string {
    const trimmed = name.trim()
    if (!trimmed) {
        return '–'
    }
    const parts = trimmed.split(/\s+/)
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return trimmed.slice(0, 2).toUpperCase()
}

const CustomersOverview: React.FC = () => {
    const [customers, setCustomers] = useState<OnboardingCustomerRow[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [pagination, setPagination] = useState<OnboardingCustomerPagination | undefined>()

    const loadPage = useCallback(async (page: number) => {
        try {
            setIsLoading(true)
            const result = await fetchOnboardingCustomers(page, ITEMS_PER_PAGE)
            setCustomers(result.customers)
            setPagination(result.pagination)
        } catch {
            showToast('Daten konnten nicht geladen werden', 'error')
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadPage(currentPage)
    }, [currentPage, loadPage])

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page)
    }, [])

    if (isLoading) {
        return (
            <div className={cx(styles['loading-container'])}>
                <div>Lade Kunden …</div>
            </div>
        )
    }

    return (
        <div className={cx(styles['overview-container'])}>
            <div className={cx(styles['overview-header'])}>
                <Heading level={1} classes={cx(styles['overview-title'])}>
                    Kunden
                </Heading>
            </div>

            {customers.length === 0 ? (
                <div className={cx(styles['empty-state'])}>
                    <h3>Keine Onboarding-Einträge</h3>
                    <p>Es liegen noch keine Formular-Einreichungen vor.</p>
                </div>
            ) : (
                <>
                    <div className={cx(styles['table-wrap'])}>
                        <div className={cx(styles['table-header'])}>
                            <span />
                            <span>Kunde</span>
                            <span>Status</span>
                        </div>

                        {customers.map((row) => {
                            const displayName = row.clubName.trim() || row.email

                            return (
                                <div key={row.submissionId} className={cx(styles['table-row'])}>
                                    <div className={cx(styles['avatar-wrap'])}>
                                        <div
                                            className={cx(
                                                styles['avatar'],
                                                !row.hasShopCustomer && styles['avatar-no-customer']
                                            )}
                                        >
                                            {getInitials(displayName)}
                                        </div>
                                        {row.cAktiv !== 'N' && row.hasShopCustomer ? (
                                            <span className={cx(styles['status-dot'])} />
                                        ) : null}
                                    </div>

                                    <div className={cx(styles['cell-identity'])}>
                                        <div className={cx(styles['row-name'])}>{displayName}</div>
                                        {row.clubName.trim() ? (
                                            <div className={cx(styles['row-email'])}>{row.email}</div>
                                        ) : null}
                                    </div>

                                    <div className={cx(styles['cell-meta'])}>
                                        <div className={cx(styles['row-badges'])}>
                                            {row.kundengruppeName ? (
                                                <span className={cx(styles['badge-group'])}>
                                                    {row.kundengruppeName}
                                                </span>
                                            ) : null}
                                            {row.cAktiv !== 'N' ? (
                                                <span className={cx(styles['badge-active'])}>
                                                    Aktiv
                                                </span>
                                            ) : (
                                                <span className={cx(styles['badge-inactive'])}>
                                                    Inaktiv
                                                </span>
                                            )}
                                            {!row.hasShopCustomer ? (
                                                <span className={cx(styles['badge-no-customer'])}>
                                                    Kein Shop-Kunde
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {pagination && pagination.totalPages > 1 ? (
                        <div className={cx(styles['pagination-container'])}>
                            <Pagination
                                totalPages={pagination.totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    )
}

export default CustomersOverview
